// Returns a list of react-hook-form errors for the given fieldName
import { DatePickerHotKeys } from './keyboardKeys';

export function getFieldErrors(fieldName = '', errors = {}) {
	// dynamic field names are required to be xxxx.dd.xxxx format per react-hook-form specs
	const dynamicInputMatches = fieldName?.match(/(.+)\.(\d+)\.(.+)/);
	if (dynamicInputMatches?.length === 4) {
		const fieldArrayName = dynamicInputMatches[1];
		const fieldArrayIndex = parseInt(dynamicInputMatches[2]);
		const dynamicFieldName = dynamicInputMatches[3];

		const dynamicFieldError = errors?.[fieldArrayName]?.[fieldArrayIndex]?.[dynamicFieldName];
		return dynamicFieldError ? dynamicFieldError : undefined;
	}

	const fieldError = errors?.[fieldName];
	return fieldError ? fieldError : undefined;
}

// Returns true/false if a given fieldName has errors
export function fieldHasErrors(fieldName, errors) {
	return typeof getFieldErrors(fieldName, errors) !== 'undefined';
}

const validateDaysInMonth = (month, day, year) => {
	switch (month) {
		// February
		case 2:
			return (day > 0 && day < 29) || (day === 29 && year % 4 === 0);
		// January, March, May, July, August, October, December
		case 1:
		case 3:
		case 5:
		case 7:
		case 8:
		case 10:
		case 12:
			return day > 0 && day <= 31;
		// April, June, September, November
		case 4:
		case 6:
		case 9:
		case 11:
			return day > 0 && day <= 30;
		default:
			return false;
	}
};

export function isValueUnset(value) {
	return value === null || value === undefined || value === '';
}

export const Validation = {
	email: {
		name: 'email',
		validate: value => {
			return isValueUnset(value) || /\b[A-Z0-9._%-]+@[A-Z0-9.-]+\.[A-Z]{2,6}\b/i.test(value);
		},
		message: 'Invalid format. Must be a valid email address format.'
	},
	integer: {
		name: 'integer',
		validate: value => {
			return isValueUnset(value) || /^\d+$/.test(value);
		},
		message: 'Invalid numeric field. Must be all numbers.'
	},
	phone: {
		name: 'phone',
		validate: value => {
			return isValueUnset(value) || /^(\d{3})-?(\d{3})-?(\d{4})$/.test(value);
		},
		message: 'Invalid phone number. Must be 10 digits or in the form NNN-NNN-NNNN.'
	},
	alphaNumeric: {
		name: 'alphaNumeric',
		validate: value => {
			return isValueUnset(value) || /^[a-z0-9]+$/i.test(value);
		},
		message: 'Invalid alphanumeric field. Must contain only characters A thru Z, or 0 thru 9.'
	},
	ssn: {
		name: 'ssn',
		validate: value => {
			if (isValueUnset(value)) {
				return true;
			}

			if (/^(?!000|666)[0-8]\d{2}([-]?)(?!00)\d{2}\1(?!0000)\d{4}$/.test(value)) {
				return !(value === '123456789' || value === '012345678' || /^(.)\1*$/.test(value));
			}

			return false;
		},
		message: 'Must be 9 digits or in the form NNN-NN-NNNN.'
	},
	date: {
		name: 'formattedDate',
		validate: value => {
			if (isValueUnset(value) || value === DatePickerHotKeys.nextDay || value === DatePickerHotKeys.today || value === DatePickerHotKeys.previousDay) {
				return true;
			}

			if (/^(0[1-9]|1[012])[/.](0[1-9]|[12]\d|3[01])[/.](19|20)\d\d$/.test(value)) {
				const [month, day, year] = value.split('/').map(x => x - 0);
				const thisYear = new Date().getFullYear();
				if (year <= thisYear + 125 && year >= thisYear - 125) {
					return validateDaysInMonth(month, day, year);
				}
			}

			return false;
		},
		message: 'Invalid date. Please enter date in mm/dd/yyyy format.'
	},
	zip: {
		name: 'USorCanadianZip',
		validate: value => {
			return isValueUnset(value) || /(^\d{5}(?:-?\d{4})?$)|(^[A-Z]\d[A-Z] \d[A-Z]\d$)/.test(value);
		},
		message: 'Invalid zip code format. Must be a valid USA/Canadian zip code.'
	},
	money: {
		name: 'money',
		validate: value => {
			return isValueUnset(value) || /(?=.*?\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/.test(value);
		},
		message: 'Invalid Monetary Amount. Must be numeric digits or in the form NNN,NNN.'
	},
	numeric: {
		name: 'numeric',
		validate: value => {
			return isValueUnset(value) || !Number.isNaN(Number(value));
		},
		message: 'Invalid numeric field. Must be numeric.'
	}
};

export const AttributeBasedValidation = {
	maxLength: value => {
		return {
			value: value,
			message: `Value must be ${value} characters or less.`
		};
	},
	minLength: value => {
		return {
			value: value,
			message: `Value must be ${value} characters or more.`
		};
	},
	max: value => {
		return {
			value: value,
			message: `Value must be less than or equal to ${value}.`
		};
	},
	min: value => {
		return {
			value: value,
			message: `Value must be greater than or equal to ${value}.`
		};
	}
};

export function buildRegisterProps(register, registerOptions = {}, fieldProps = {}) {
	const { name, required, validators, ...rest } = registerOptions;
	if (!name || !register) {
		return {};
	}

	let internalRegisterOptions = {
		required: !!required,
		shouldUnregister: true,
		...rest
	};

	Object.keys(AttributeBasedValidation).forEach(key => {
		if (Object.prototype.hasOwnProperty.call(fieldProps, key) && fieldProps[key]) {
			internalRegisterOptions[key] = AttributeBasedValidation[key](fieldProps[key]);
		}
	});

	if (Array.isArray(validators) && validators.length > 0) {
		internalRegisterOptions['validate'] = {};
		validators.forEach(validator => {
			internalRegisterOptions['validate'][validator.name] = value => validator.validate(value) || validator.message;
		});
	}

	// returns { onChange, onBlur, name, ref }
	return register(name, internalRegisterOptions);
}

export function isInputChecked(element) {
	let isChecked = false;
	if (element) {
		isChecked = element.checked || isChecked;
		if (!isChecked && element.type === 'radio' && element.form) {
			for (const radio of element.form[element.name]) {
				if (radio.checked) {
					isChecked = true;
					break;
				}
			}
		}
	}

	return isChecked;
}

export function submitFormRef(formRef) {
	formRef && formRef.current && formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
}
